import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  formatPlatformCount, getPlatformStats, newestPlatformStats, PlatformStats,
  preloadedPlatformStats,
} from '../services/platformStatsService';

import './PlatformProof.css';

function usePlatformStats(): PlatformStats | null {
  const [stats, setStats] = useState(preloadedPlatformStats);

  useEffect(() => {
    // The static renderer injects one snapshot for matching HTML and hydration.
    if (/ReactSnap/i.test(navigator.userAgent)) return;
    const controller = new globalThis.AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);

    void getPlatformStats(controller.signal).then((incoming) => {
      if (!controller.signal.aborted) setStats((previous) => newestPlatformStats(previous, incoming));
    }).catch(() => undefined).finally(() => window.clearTimeout(timeout));

    return () => { controller.abort(); window.clearTimeout(timeout); };
  }, []);

  return stats;
}

function PlatformStat({ id, value, label }: {
  id: string; value: number | null; label: string;
}) {
  return (
    <div className="pp-stat" data-platform-stat={id}>
      <span className="pp-stat__value" title={value === null ? undefined : value.toLocaleString('en-US')}>
        {value === null ? '—' : formatPlatformCount(value)}
      </span>
      <span className="pp-stat__label">{label}</span>
      {value === null && <span className="pp-stat__detail">Data unavailable</span>}
    </div>
  );
}

export const PlatformProof: React.FC = () => {
  const stats = usePlatformStats();
  const available = stats && [
    stats.estimated_screens_generated, stats.estimated_tokens_processed, stats.countries_served,
  ]
    .some((value) => value !== null);

  return (
    <section className="pp-strip" aria-labelledby="platform-proof-heading" data-platform-stats={available ? 'ready' : 'unavailable'}>
      <h2 id="platform-proof-heading" className="pp-strip__sr">Platform track record</h2>
      <div className="pp-strip__stats">
        <PlatformStat id="screens" value={stats?.estimated_screens_generated ?? null} label="Screens generated" />
        <PlatformStat id="tokens" value={stats?.estimated_tokens_processed ?? null} label="Tokens processed" />
        <PlatformStat id="countries" value={stats?.countries_served ?? null} label="Countries served" />
      </div>
      <p className="pp-strip__kicker">
        Explore what people are building in <Link to="/explore">the public gallery</Link>.
      </p>
    </section>
  );
};
